// Form de pago — Fase E2: modalidad normal/adelantado, descuento (MONTO XOR
// PORCENTAJE), foto de boleta con OCR (human-in-the-loop: evidencia que el
// operador revisa), and available customer credit.
// Geolocalización (regla 2026-08-16): la ubicación NO se ingresa a mano —
// el cobrador la captura del dispositivo (navigator.geolocation) al abrir el
// form; sin permiso no puede registrar pagos. El admin no captura GPS: el backend
// usa la ubicación del cliente como snapshot cuando está disponible.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { CSSProperties } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Segmented,
  Space,
  Typography,
  Upload,
  message,
  theme,
} from 'antd';
import type { FormInstance, UploadProps } from 'antd';
import {
  CameraOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { useT } from '../../../locales';
import { useHasCobradorPaymentScope } from '../../../auth/useHasPermission';
import { ClienteSelect } from '../../../components/ClienteSelect';
import { CatalogoSelect } from '../../../components/CatalogoSelect';
import { useCatalogos } from '../../catalogos/api/useCatalogos';
import {
  useCliente,
  useClienteResumen,
  useClienteServicios,
  usePreviewClienteUbicacion,
} from '../../clientes/api/useClientes';
import { useCobradorClienteContext } from '../../cobrador/api/useCobrador';
import { useBoletaOcr, usePagoFifoPreview, usePagoPrepagoPreview } from '../api/usePagos';
import { getBoletaUploadErrorMessage } from '../lib/boleta-error';
import {
  BOLETA_IMAGE_ACCEPT,
  BOLETA_MAX_INPUT_BYTES,
  PAGO_MODALIDAD,
  TIPO_SUSCRIPCION,
  type BoletaOcrResult,
  type CatalogoRef,
  type ClienteRef,
  type ClienteResumenResponse,
  type PagoModalidad,
} from '@aid/shared';
import { formatCurrency } from '../../../lib/format';
import dayjs from 'dayjs';
import { PagoFifoPreview } from './PagoFifoPreview';
import {
  MODO_FACTURAS,
  PagoFacturasPicker,
  filtrarFacturasPagables,
  type ModoFacturas,
} from './PagoFacturasPicker';

const { Text } = Typography;

interface ResumenFinancieroProps {
  resumen: Pick<ClienteResumenResponse, 'deudaPendiente' | 'creditoDisponible'> | undefined;
  loading: boolean;
  error: boolean;
}

interface ResumenMetricaProps {
  label: string;
  value: ReactNode;
  color?: string;
}

function parseMoney(value: string | number | null | undefined): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function hasDetectedValue(value: string | null): value is string {
  return value != null && value.trim().length > 0;
}

function parseDetectedDate(value: string | null) {
  if (!hasDetectedValue(value)) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

function ResumenMetrica({ label, value, color }: ResumenMetricaProps) {
  const { token } = theme.useToken();
  return (
    <div style={{ minWidth: 0 }}>
      <Text
        type="secondary"
        style={{ display: 'block', fontSize: 12, lineHeight: 1.4, marginBottom: token.marginXXS }}
      >
        {label}
      </Text>
      <Text
        style={{
          display: 'block',
          fontSize: 16,
          fontWeight: 600,
          lineHeight: 1.3,
          fontVariantNumeric: 'tabular-nums',
          color: color ?? token.colorText,
        }}
      >
        {value}
      </Text>
    </div>
  );
}

function ResumenFinanciero({ resumen, loading, error }: ResumenFinancieroProps) {
  const t = useT();
  const { token } = theme.useToken();
  const deudaPendiente = parseMoney(resumen?.deudaPendiente);
  // Applicable credit (ledger lots), never the statement net: the net also
  // counts prepaid package reserves (see ClienteResumenResponse).
  const creditoAplicable = parseMoney(resumen?.creditoDisponible);
  const datosDisponibles = !loading && !error && resumen !== undefined;
  // La separación vertical del bloque (divider/sección) la da el gutter del
  // Row (~20px); aquí solo vive la línea divisoria y su aire interno.
  const summaryStyle: CSSProperties = {
    borderTop: `1px solid ${token.colorSplit}`,
    borderBottom: `1px solid ${token.colorSplit}`,
    paddingBlock: token.paddingSM,
  };

  return (
    <div aria-busy={loading} style={summaryStyle}>
      <Text strong style={{ display: 'block', marginBottom: token.marginSM }}>
        {t('pages.pagos.resumenFinanciero')}
      </Text>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: token.marginSM,
        }}
      >
        <ResumenMetrica
          label={t('pages.pagos.deudaPendiente')}
          value={loading ? '…' : error || !datosDisponibles ? '—' : formatCurrency(deudaPendiente)}
          color={datosDisponibles && deudaPendiente > 0 ? token.colorError : undefined}
        />
        <ResumenMetrica
          label={t('pages.pagos.saldoAFavor')}
          value={
            loading ? '…' : error || !datosDisponibles ? '—' : formatCurrency(creditoAplicable)
          }
          color={datosDisponibles && creditoAplicable > 0 ? token.colorSuccess : undefined}
        />
      </div>
      {error ? (
        <Text
          type="secondary"
          style={{ display: 'block', fontSize: 12, lineHeight: 1.5, marginTop: token.marginSM }}
        >
          {t('pages.pagos.resumenError')}
        </Text>
      ) : null}
    </div>
  );
}

/** Tipos de pago que admiten boleta (foto de deposito/transferencia). */
const TIPOS_CON_BOLETA = ['DEPOSITO', 'BANCO', 'TRANSFERENCIA'];
const REFERENCE_SOURCE = {
  NONE: 'none',
  OCR: 'ocr',
  MANUAL: 'manual',
} as const;
type ReferenceSource = (typeof REFERENCE_SOURCE)[keyof typeof REFERENCE_SOURCE];
/** Estados de servicio que facturan (mismo contrato que el aplicador). */
const ESTADOS_FACTURABLES = ['ACTIVO', 'BAJA_AL_CORTE'];

interface PagoFormProps {
  mode: 'create' | 'edit';
  initialValues?: Record<string, unknown> | null;
  preselectedClienteId?: number;
  /** Compact client data from the cobrador worklist; avoids an admin lookup. */
  preselectedCliente?: ClienteRef;
  onSubmit: (values: Record<string, unknown>) => void;
  loading?: boolean;
  form?: FormInstance;
  hideSubmit?: boolean;
  /** Notifica al contenedor si el submit debe bloquearse por ubicación u OCR. */
  onLocationBlockedChange?: (blocked: boolean) => void;
  /** Notifica si el cobrador pidió actualizar la ubicación del cliente con este pago. */
  onClientLocationUpdateChange?: (shouldUpdate: boolean) => void;
}

/** Estados del bloque de geolocalización del cobrador. */
type GeoEstado = 'inactivo' | 'solicitando' | 'capturada' | 'denegada' | 'noSoportada';

interface Ubicacion {
  lat: number;
  lng: number;
  accuracy: number | null;
}

type ClientLocationDecision = 'pending' | 'update';

function modalidadPorTipoSuscripcion(
  tipoSuscripcion: CatalogoRef | null | undefined,
): PagoModalidad | null {
  switch (tipoSuscripcion?.key) {
    case TIPO_SUSCRIPCION.MENSUAL:
      return PAGO_MODALIDAD.NORMAL;
    case TIPO_SUSCRIPCION.PREPAGO:
      return PAGO_MODALIDAD.ADELANTADO;
    default:
      return null;
  }
}

function solicitarUbicacion(
  hasCobradorPermission: boolean,
  setGeoEstado: (estado: GeoEstado) => void,
  setUbicacion: (ubicacion: Ubicacion) => void,
) {
  if (!hasCobradorPermission) return;
  if (!navigator.geolocation) {
    setGeoEstado('noSoportada');
    return;
  }
  setGeoEstado('solicitando');
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      setUbicacion({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
      });
      setGeoEstado('capturada');
    },
    () => setGeoEstado('denegada'),
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
  );
}

export function PagoForm({
  mode,
  initialValues,
  preselectedClienteId,
  onSubmit,
  loading,
  form: externalForm,
  hideSubmit = false,
  onLocationBlockedChange,
  onClientLocationUpdateChange,
  preselectedCliente,
}: PagoFormProps) {
  const t = useT();
  const fixedClienteId = preselectedClienteId ?? preselectedCliente?.id;
  const [internalForm] = Form.useForm();
  const form = externalForm ?? internalForm;
  const { token } = theme.useToken();
  // El ritmo vertical lo gobierna el Row (gutter [16, 20] con tokens); los
  // Form.Item solo resetean su margin por defecto (24px) para no duplicarlo.
  const itemStyle: CSSProperties = { marginBottom: 0 };

  const isCobradorPaymentScope = useHasCobradorPaymentScope();
  const [geoEstado, setGeoEstado] = useState<GeoEstado>('inactivo');
  const [ubicacion, setUbicacion] = useState<Ubicacion | null>(null);
  const [clientLocationDecision, setClientLocationDecision] =
    useState<ClientLocationDecision>('pending');

  const [ocrResult, setOcrResult] = useState<BoletaOcrResult | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [ocrInFlight, setOcrInFlight] = useState(false);
  const ocrInFlightRef = useRef(false);
  const ocrGenerationRef = useRef(0);
  const activeOcrGenerationRef = useRef<number | null>(null);
  const referenceSourceRef = useRef<ReferenceSource>(REFERENCE_SOURCE.NONE);
  const lastOcrReferenceRef = useRef<string | undefined>(undefined);
  const previewUrlRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const initializedPrepaidCandidateRef = useRef<number | null>(null);
  const prepagoModalidadInicializadaRef = useRef<number | null>(null);
  const previousModalidadRef = useRef<PagoModalidad | null>(null);

  const modalidad = (Form.useWatch('modalidad', form) ?? PAGO_MODALIDAD.NORMAL) as PagoModalidad;
  const idCliente = Form.useWatch('idCliente', form) as number | null | undefined;
  const idTipoPago = Form.useWatch('idTipoPago', { form, preserve: true }) as
    number | null | undefined;
  const meses = Form.useWatch('mesesAdelantado', form) as number | null | undefined;
  const descuentoTipo = Form.useWatch('descuentoTipo', form) as 'MONTO' | 'PORCENTAJE' | null;
  const descuentoValor = Form.useWatch('descuentoValor', form) as number | null | undefined;
  const valorIngresado = Form.useWatch('valor', form) as number | string | null | undefined;
  const fechaCobro = Form.useWatch('fechaCobro', form) as dayjs.Dayjs | string | null | undefined;
  const reemplazarFacturaActual = Form.useWatch('reemplazarFacturaActual', form) === true;

  const clienteId = idCliente != null && Number.isFinite(Number(idCliente)) ? Number(idCliente) : 0;
  const paymentLocation = ubicacion ? { latitud: ubicacion.lat, longitud: ubicacion.lng } : null;

  const clienteAdminQuery = useCliente(clienteId, {
    enabled: !isCobradorPaymentScope,
  });
  const {
    data: cobradorContext,
    isLoading: cobradorContextLoading,
    isError: cobradorContextError,
  } = useCobradorClienteContext(clienteId, { enabled: isCobradorPaymentScope });
  const ubicacionPreview = usePreviewClienteUbicacion(clienteId, paymentLocation);
  const tipoSuscripcion = isCobradorPaymentScope
    ? cobradorContext?.tipoSuscripcion
    : clienteAdminQuery.data?.tipoSuscripcion;
  const esPrepago = tipoSuscripcion?.key === TIPO_SUSCRIPCION.PREPAGO;
  const modalidadPorSuscripcion = modalidadPorTipoSuscripcion(tipoSuscripcion);
  const tipoSuscripcionLoading = isCobradorPaymentScope
    ? cobradorContextLoading
    : clienteAdminQuery.isLoading;

  const {
    data: resumen,
    isLoading: resumenLoading,
    isError: resumenError,
  } = useClienteResumen(clienteId, { enabled: !isCobradorPaymentScope });
  const { data: serviciosData } = useClienteServicios(
    clienteId,
    { limit: 100, offset: 0 },
    { enabled: !isCobradorPaymentScope },
  );

  const { data: tiposPagoAdmin } = useCatalogos('TIPO_PAGO', {
    enabled: !isCobradorPaymentScope,
  });
  const tiposPago = isCobradorPaymentScope ? cobradorContext?.tiposPago : tiposPagoAdmin;
  const resumenFinanciero = isCobradorPaymentScope ? cobradorContext : resumen;
  const resumenFinancieroLoading = isCobradorPaymentScope ? cobradorContextLoading : resumenLoading;
  const resumenFinancieroError = isCobradorPaymentScope ? cobradorContextError : resumenError;
  const boletaOcr = useBoletaOcr();

  const cobradorClienteId = fixedClienteId ?? (clienteId || undefined);
  const cobradorCliente = cobradorContext?.cliente ?? preselectedCliente;
  const cobradorClienteLabel = `${cobradorCliente?.nombre ?? `#${cobradorClienteId ?? ''}`}${
    cobradorCliente?.ip ? ` (${cobradorCliente.ip})` : ''
  }`;

  const tipoPagoKey =
    idTipoPago != null
      ? (tiposPago ?? []).find((c) => Number(c.id) === Number(idTipoPago))?.key
      : null;
  const boletaVisible = tipoPagoKey != null && TIPOS_CON_BOLETA.includes(tipoPagoKey);
  const previousBoletaVisibleRef = useRef(boletaVisible);

  // Total planificado = SUM(planes de servicios activos facturables) x meses
  // (mismo calculo que el aplicador en el backend).
  const precioMensual = isCobradorPaymentScope
    ? parseMoney(cobradorContext?.precioMensual)
    : ((serviciosData?.data ?? []).reduce((acc, s) => {
        if (
          !s.tipoServicio?.generaCobro ||
          !s.plan?.precio ||
          (s.estado?.key && !ESTADOS_FACTURABLES.includes(s.estado.key))
        ) {
          return acc;
        }
        const precio = Number(s.plan.precio);
        return Number.isFinite(precio) ? acc + precio : acc;
      }, 0) ?? 0);

  const mesesValidos = meses != null && Number.isFinite(Number(meses)) ? Number(meses) : 0;
  const totalPlanificado = precioMensual * mesesValidos;
  const descuentoAplicado =
    descuentoTipo === 'MONTO'
      ? Number(descuentoValor ?? 0)
      : descuentoTipo === 'PORCENTAJE'
        ? totalPlanificado * (Number(descuentoValor ?? 0) / 100)
        : 0;
  const totalAPagar = Math.max(0, totalPlanificado - descuentoAplicado);

  const montoIngresado = parseMoney(valorIngresado);

  // S6/S5: read here because the forecast request needs them; the amount
  // ceiling is computed below, once the payable list is known.
  const modoFacturas = Form.useWatch('modoFacturas', form) as ModoFacturas | undefined;
  const facturasSeleccionadas = (Form.useWatch('idFacturasPago', form) ?? []) as number[];

  // S5.7/S7: the probe carries the invoice type additively, so the prepaid
  // screen can offer the client's open periodic and extraordinary invoices. The
  // probe amount is irrelevant: the selector reads the rows. It runs FIRST
  // because the forecast below needs the payable list to know what is marked.
  const facturasPreview = usePagoFifoPreview({
    clienteId,
    montoPago: 0.01,
    modalidad: PAGO_MODALIDAD.NORMAL,
    enabled: esPrepago,
  });
  const facturasPagables = filtrarFacturasPagables(facturasPreview.data?.facturas);
  const facturasPreviewLoading =
    esPrepago && (facturasPreview.isLoading || facturasPreview.isFetching);
  const prepagoConFacturas = esPrepago && facturasPagables.length > 0;
  const facturasCubiertas = facturasPreview.data?.facturasCubiertas ?? [];
  const prepagoConCubiertas = esPrepago && facturasCubiertas.length > 0;
  // Only ids still present among the open payable invoices count as a target.
  const facturasSeleccionadasValidas = facturasPagables
    .filter((factura) => facturasSeleccionadas.includes(factura.id))
    .map((factura) => factura.id);
  // S5: the amount ceiling is the sum of the MARKED invoices' balances.
  const totalSeleccionado = facturasPagables
    .filter((factura) => facturasSeleccionadas.includes(factura.id))
    .reduce((acc, factura) => acc + Number(factura.saldoAntes ?? 0), 0);
  // The punctual mode with nothing marked has NO plan yet: it must never be
  // forecast as an automatic FIFO, which is the misleading screen this gate
  // prevents (an empty selection is not a selection).
  const esperandoMarcas =
    esPrepago &&
    modoFacturas === MODO_FACTURAS.PUNTUAL &&
    facturasSeleccionadasValidas.length === 0;

  const fifoPreview = usePagoFifoPreview({
    clienteId,
    montoPago: montoIngresado,
    modalidad,
    // Shown for EVERY client and every payment type: a prepaid client is MIXED
    // (the package plus the monthly invoices the cron generates when it lapses)
    // and any payment — cash without change included — can leave a surplus as
    // the client's credit.
    idFacturasPago:
      esPrepago && modoFacturas === MODO_FACTURAS.PUNTUAL
        ? facturasSeleccionadasValidas
        : undefined,
    enabled: !esperandoMarcas,
  });
  const fifoPreviewLoading =
    fifoPreview.isLoading || fifoPreview.isFetching || fifoPreview.isDebouncing;
  // Open invoices the operator left unmarked: while any exists, a surplus is
  // refused (money does not become credit with payable debt around), and the
  // screen offers to mark them.
  const pendientesSinMarcar = facturasPagables.filter(
    (factura) => !facturasSeleccionadasValidas.includes(factura.id),
  );
  const validarTopeMarcado = async (_regla: unknown, valor: unknown) => {
    // The ceiling belongs to a NORMAL targeted payment only: a package payment
    // must not be blocked by leftover invoice state.
    if (
      !esPrepago ||
      modalidad !== PAGO_MODALIDAD.NORMAL ||
      modoFacturas !== MODO_FACTURAS.PUNTUAL
    ) {
      return;
    }
    if (facturasSeleccionadasValidas.length === 0) return; // the selection rule speaks
    const monto = Number(valor ?? 0);
    if (!Number.isFinite(monto) || monto <= 0) return;
    const sobrante = monto - totalSeleccionado;
    if (sobrante <= 0.005 || pendientesSinMarcar.length === 0) return;
    throw new Error(
      t('pages.pagos.montoExcedeMarcado', {
        seleccionado: formatCurrency(totalSeleccionado),
        sobrante: formatCurrency(sobrante),
      }),
    );
  };
  // A PREPAGO client with an open invoice may target it as a NORMAL payment;
  // without one the package path stays forced exactly as today.
  // A refresh must never turn an explicitly chosen invoice payment into a
  // package purchase. Keep NORMAL visible and reject stale targets instead.
  const pagoFacturaEnCurso =
    esPrepago &&
    prepagoModalidadInicializadaRef.current === clienteId &&
    modalidad === PAGO_MODALIDAD.NORMAL;
  const modalidadBloqueada =
    prepagoConFacturas || pagoFacturaEnCurso ? null : modalidadPorSuscripcion;

  const fechaBase =
    fechaCobro && dayjs(fechaCobro).isValid() ? dayjs(fechaCobro).format('YYYY-MM-DD') : undefined;
  const prepagoPreview = usePagoPrepagoPreview({
    clienteId,
    mesesAdelantado: mesesValidos,
    fechaBase,
    enabled: modalidad === PAGO_MODALIDAD.ADELANTADO && clienteId > 0,
  });
  const facturaActual = prepagoPreview.data?.facturaActual ?? null;
  const prepagoPreviewLoading = prepagoPreview.isLoading || prepagoPreview.isFetching;
  const prepagoPreviewBlocked =
    modalidad === PAGO_MODALIDAD.ADELANTADO &&
    clienteId > 0 &&
    mesesValidos > 0 &&
    (prepagoPreviewLoading || prepagoPreview.isError || prepagoPreview.data === undefined);

  const ocrPending = ocrInFlight || boletaOcr.isPending;

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        idCliente: initialValues.idCliente,
        idTipoPago: initialValues.idTipoPago,
        valor: initialValues.valor ? Number(initialValues.valor) : undefined,
        comentario: initialValues.comentarios ?? '',
        // Solo se pisa la fecha cuando hay una persistida; si el pago no la
        // tiene, se conserva el default de creación (HOY).
        ...(initialValues.fechaPago
          ? { fechaCobro: dayjs(initialValues.fechaPago as string) }
          : {}),
        modalidad: initialValues.modalidad ?? 'NORMAL',
      });
    } else if (fixedClienteId != null) {
      form.setFieldValue('idCliente', fixedClienteId);
    }
  }, [initialValues, fixedClienteId, form]);

  useEffect(() => {
    if (modalidadBloqueada !== null) {
      if (form.getFieldValue('modalidad') !== modalidadBloqueada) {
        form.setFieldValue('modalidad', modalidadBloqueada);
      }
      return;
    }
    // S5.7: the package path stays the default for a PREPAGO client even when
    // the invoice choice is available.
    if (prepagoConFacturas && prepagoModalidadInicializadaRef.current !== clienteId) {
      form.setFieldValue('modalidad', PAGO_MODALIDAD.ADELANTADO);
      prepagoModalidadInicializadaRef.current = clienteId;
    }
  }, [form, modalidadBloqueada, prepagoConFacturas, clienteId]);

  // Switching back to the package path drops the targeted invoice: the id can
  // never survive an ADELANTADO payload.
  useEffect(() => {
    const previous = previousModalidadRef.current;
    previousModalidadRef.current = modalidad;
    if (previous === PAGO_MODALIDAD.NORMAL && modalidad === PAGO_MODALIDAD.ADELANTADO) {
      form.setFieldsValue({
        modoFacturas: undefined,
        idFacturasPago: undefined,
        idFacturaPago: undefined,
      });
    }
  }, [form, modalidad]);

  // A different client must never inherit a previous client's invoice target.
  useEffect(() => {
    form.setFieldsValue({
      modoFacturas: undefined,
      idFacturasPago: undefined,
      idFacturaPago: undefined,
    });
  }, [clienteId, form]);

  useEffect(() => {
    if (modalidad !== PAGO_MODALIDAD.ADELANTADO) {
      initializedPrepaidCandidateRef.current = null;
      form.setFieldsValue({ idFacturaActual: undefined, reemplazarFacturaActual: false });
      return;
    }
    if (prepagoPreview.data === undefined) return;

    if (facturaActual) {
      form.setFieldValue('idFacturaActual', facturaActual.id);
      if (initializedPrepaidCandidateRef.current !== facturaActual.id) {
        form.setFieldValue('reemplazarFacturaActual', true);
        initializedPrepaidCandidateRef.current = facturaActual.id;
      }
      return;
    }

    initializedPrepaidCandidateRef.current = null;
    form.setFieldsValue({ idFacturaActual: undefined, reemplazarFacturaActual: false });
  }, [facturaActual, modalidad, prepagoPreview.data, form]);

  // La geolocalización se pide AL MONTAR el form (no al elegir cliente):
  // el modal usa destroyOnHidden, así que cada apertura re-monta el form y
  // dispara la captura. Es el momento con mejor UX: el cobrador ve el estado
  // mientras completa los otros campos, y tras el primer permiso el browser
  // resuelve la promesa al instante (permiso cacheado).
  useEffect(() => {
    if (isCobradorPaymentScope)
      solicitarUbicacion(isCobradorPaymentScope, setGeoEstado, setUbicacion);
  }, [isCobradorPaymentScope]);

  useEffect(() => {
    setClientLocationDecision('pending');
    onClientLocationUpdateChange?.(false);
  }, [
    clienteId,
    paymentLocation?.latitud,
    paymentLocation?.longitud,
    onClientLocationUpdateChange,
  ]);

  const chooseClientLocationDecision = (shouldUpdate: boolean) => {
    const decision: ClientLocationDecision = shouldUpdate ? 'update' : 'pending';
    setClientLocationDecision(decision);
    onClientLocationUpdateChange?.(shouldUpdate);
  };

  // El bloque de submit lo decide este form; si el botón vive afuera (modal
  // con hideSubmit) se propaga para que el footer se deshabilite igual.
  const ubicacionBloqueada = isCobradorPaymentScope && geoEstado !== 'capturada';
  const submitBlocked = ubicacionBloqueada || ocrPending || prepagoPreviewBlocked;
  useEffect(() => {
    onLocationBlockedChange?.(submitBlocked);
  }, [onLocationBlockedChange, submitBlocked]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      ocrGenerationRef.current += 1;
      const currentPreviewUrl = previewUrlRef.current;
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
        previewUrlRef.current = null;
      }
    };
  }, []);

  // En ADELANTADO el valor NO se tipea: se calcula (planificado - descuento)
  // y el backend lo valida contra el mismo calculo.
  useEffect(() => {
    if (modalidad === PAGO_MODALIDAD.ADELANTADO) {
      form.setFieldsValue({ valor: Number(totalAPagar.toFixed(2)) });
    }
  }, [modalidad, totalAPagar, form]);

  const handleFinish = (values: Record<string, unknown>) => {
    if (ocrInFlightRef.current || ocrPending) {
      message.warning(t('pages.pagos.ocrPending'));
      return;
    }

    // Defensa en profundidad: aunque el botón esté deshabilitado, nunca se
    // crea un pago de cobrador sin coordenadas (el backend también rechaza).
    if (isCobradorPaymentScope && !ubicacion) {
      message.warning(t('pages.pagos.geoRequerida'));
      return;
    }

    const payload: Record<string, unknown> = { ...values };
    if (modalidadBloqueada !== null) payload.modalidad = modalidadBloqueada;
    if (isCobradorPaymentScope && ubicacion) {
      payload.latitud = ubicacion.lat;
      payload.longitud = ubicacion.lng;
    } else {
      // Admin: el payload va sin lat/lng; el backend toma la ubicación del cliente
      // como snapshot cuando existe.
      delete payload.latitud;
      delete payload.longitud;
    }
    if (payload.fechaCobro)
      payload.fechaCobro = dayjs(payload.fechaCobro as dayjs.Dayjs).format('YYYY-MM-DD');
    // A targeted selection only counts while every id is still among the open
    // payable invoices: an id that disappeared on refresh is dropped instead of
    // being sent, so the screen can never name an obsolete or foreign invoice.
    if (esPrepago && modalidad === PAGO_MODALIDAD.NORMAL) {
      // A stale or unavailable lookup cannot validate a target: block instead
      // of sending an obsolete invoice or silently turning it into FIFO.
      if (facturasPreviewLoading || facturasPreview.isError) return;
      // With no open invoice a NORMAL payment would only create credit, so the
      // screen keeps the operator on the package path and explains why.
      if (facturasPagables.length === 0) return;
      if (modoFacturas === undefined) {
        form.setFields([
          { name: 'modoFacturas', errors: [t('pages.pagos.facturas.modoRequired')] },
        ]);
        return;
      }
      if (modoFacturas === MODO_FACTURAS.PUNTUAL) {
        if (facturasSeleccionadasValidas.length === 0) {
          form.setFields([
            { name: 'idFacturasPago', errors: [t('pages.pagos.facturas.seleccionRequired')] },
          ]);
          return;
        }
        // The same rule the live validator applies: a surplus is refused only
        // while a payable invoice is left unmarked. With nothing left to mark it
        // becomes the client's credit (the backend creates the lot).
        const sobrante = Number(values.valor ?? 0) - totalSeleccionado;
        if (sobrante > 0.005 && pendientesSinMarcar.length > 0) {
          form.setFields([
            {
              name: 'valor',
              errors: [
                t('pages.pagos.montoExcedeMarcado', {
                  seleccionado: formatCurrency(totalSeleccionado),
                  sobrante: formatCurrency(sobrante),
                }),
              ],
            },
          ]);
          return;
        }
      }
    }
    const modalidadEfectiva = modalidad;
    if (modalidadEfectiva === PAGO_MODALIDAD.ADELANTADO) {
      payload.valor = totalAPagar.toFixed(2);
      payload.totalPlanificado = totalPlanificado.toFixed(2);
      if (facturaActual !== null && values.reemplazarFacturaActual === true) {
        payload.idFacturaActual = facturaActual.id;
        payload.reemplazarFacturaActual = true;
      } else {
        delete payload.idFacturaActual;
        payload.reemplazarFacturaActual = false;
      }
      if (!descuentoTipo) {
        payload.descuentoTipo = null;
        payload.descuentoValor = null;
      }
      // El paquete nunca apunta a facturas puntuales.
      delete payload.idFacturaPago;
      delete payload.idFacturasPago;
    } else {
      payload.modalidad = 'NORMAL';
      payload.mesesAdelantado = undefined;
      payload.descuentoTipo = null;
      payload.descuentoValor = null;
      payload.totalPlanificado = undefined;
      delete payload.idFacturaActual;
      delete payload.reemplazarFacturaActual;
      delete payload.idFacturaPago;
      if (
        esPrepago &&
        modoFacturas === MODO_FACTURAS.PUNTUAL &&
        facturasSeleccionadasValidas.length > 0
      ) {
        payload.idFacturasPago = facturasSeleccionadasValidas;
      } else {
        delete payload.idFacturasPago;
      }
      if (payload.valor !== undefined) payload.valor = Number(payload.valor).toFixed(2);
    }
    if (!boletaVisible) {
      delete payload.archivoBoleta;
      delete payload.referenciaBoleta;
      delete payload.montoBoleta;
      delete payload.fechaBoleta;
    } else if (!ocrResult && !payload.archivoBoleta) {
      // El backend admite evidencia basada únicamente en una referencia.
      delete payload.archivoBoleta;
      delete payload.montoBoleta;
      delete payload.fechaBoleta;
    }
    onSubmit(payload);
  };

  const clearBoletaFields = () => {
    const preserveManualReference = referenceSourceRef.current === REFERENCE_SOURCE.MANUAL;
    form.setFieldsValue({
      archivoBoleta: undefined,
      ...(preserveManualReference ? {} : { referenciaBoleta: undefined }),
      montoBoleta: undefined,
      fechaBoleta: undefined,
    });
    if (!preserveManualReference) {
      referenceSourceRef.current = REFERENCE_SOURCE.NONE;
      lastOcrReferenceRef.current = undefined;
    }
  };

  const resetBoletaFields = () => {
    referenceSourceRef.current = REFERENCE_SOURCE.NONE;
    lastOcrReferenceRef.current = undefined;
    form.setFieldsValue({
      archivoBoleta: undefined,
      referenciaBoleta: undefined,
      montoBoleta: undefined,
      fechaBoleta: undefined,
    });
  };

  const revokePreview = () => {
    const currentPreviewUrl = previewUrlRef.current;
    if (!currentPreviewUrl) return;
    URL.revokeObjectURL(currentPreviewUrl);
    previewUrlRef.current = null;
  };

  const handleUpload: UploadProps['beforeUpload'] = (file) => {
    if (ocrInFlightRef.current || boletaOcr.isPending) return false;

    if (file.size > BOLETA_MAX_INPUT_BYTES) {
      message.error(
        t('pages.pagos.ocrTooLarge', {
          max: Math.round(BOLETA_MAX_INPUT_BYTES / (1024 * 1024)),
        }),
      );
      return false;
    }

    const currentReference = form.getFieldValue('referenciaBoleta') as string | undefined;
    const referenceWasChangedOutsideInput =
      referenceSourceRef.current === REFERENCE_SOURCE.OCR &&
      currentReference !== lastOcrReferenceRef.current;
    const referenceWasPrefilled =
      referenceSourceRef.current === REFERENCE_SOURCE.NONE &&
      typeof currentReference === 'string' &&
      currentReference.trim().length > 0;
    if (referenceWasChangedOutsideInput || referenceWasPrefilled) {
      referenceSourceRef.current = REFERENCE_SOURCE.MANUAL;
    }

    const requestGeneration = ocrGenerationRef.current + 1;
    ocrGenerationRef.current = requestGeneration;
    activeOcrGenerationRef.current = requestGeneration;
    ocrInFlightRef.current = true;
    setOcrInFlight(true);

    // Preview local inmediata.
    revokePreview();
    const nextPreviewUrl = URL.createObjectURL(file);
    previewUrlRef.current = nextPreviewUrl;
    setPreviewUrl(nextPreviewUrl);
    setOcrResult(null);
    clearBoletaFields();

    const isCurrentRequest = () =>
      mountedRef.current &&
      requestGeneration === ocrGenerationRef.current &&
      activeOcrGenerationRef.current === requestGeneration &&
      ocrInFlightRef.current;
    const settleRequest = () => {
      if (activeOcrGenerationRef.current !== requestGeneration) return;
      activeOcrGenerationRef.current = null;
      ocrInFlightRef.current = false;
      if (mountedRef.current) setOcrInFlight(false);
    };

    const handleOcrError = (error?: unknown) => {
      if (isCurrentRequest()) {
        setOcrResult(null);
        clearBoletaFields();
        message.error(getBoletaUploadErrorMessage(error) ?? t('pages.pagos.ocrError'));
      }
      settleRequest();
    };

    try {
      boletaOcr.mutate(file, {
        onSuccess: (resultado) => {
          if (isCurrentRequest()) {
            const preserveManualReference = referenceSourceRef.current === REFERENCE_SOURCE.MANUAL;
            form.setFieldsValue({
              archivoBoleta: resultado.archivo,
              ...(preserveManualReference
                ? {}
                : { referenciaBoleta: resultado.referencia ?? undefined }),
              montoBoleta: resultado.monto ?? undefined,
              fechaBoleta: resultado.fecha ?? undefined,
            });
            lastOcrReferenceRef.current = resultado.referencia ?? undefined;
            if (!preserveManualReference) {
              referenceSourceRef.current = hasDetectedValue(resultado.referencia)
                ? REFERENCE_SOURCE.OCR
                : REFERENCE_SOURCE.NONE;
            }
            setOcrResult(resultado);
            if (resultado.ocr_estado === 'FALLIDO') {
              message.warning(t('pages.pagos.ocrNoDisponible'));
            }
          }
          settleRequest();
        },
        onError: handleOcrError,
      });
    } catch (error) {
      handleOcrError(error);
    }
    return false; // Upload manual via mutation (multipart a /pagos/boleta/ocr).
  };

  const handleRemoveBoleta = () => {
    if (ocrInFlightRef.current || ocrPending) return;

    ocrGenerationRef.current += 1;
    setOcrResult(null);
    revokePreview();
    setPreviewUrl(null);
    resetBoletaFields();
  };

  useEffect(() => {
    const becameInapplicable = previousBoletaVisibleRef.current && !boletaVisible;
    previousBoletaVisibleRef.current = boletaVisible;
    if (!becameInapplicable) return;

    ocrGenerationRef.current += 1;
    setOcrResult(null);
    const currentPreviewUrl = previewUrlRef.current;
    if (currentPreviewUrl) {
      URL.revokeObjectURL(currentPreviewUrl);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    resetBoletaFields();
  }, [boletaVisible, form]);

  const ocrFallido = ocrResult?.ocr_estado === 'FALLIDO';
  const rawDetectedAmount = ocrResult?.monto ?? null;
  const detectedAmount = parseMoney(rawDetectedAmount);
  const rawDetectedDate = ocrResult?.fecha ?? null;
  const detectedDate = parseDetectedDate(rawDetectedDate);
  const rawDetectedReference = ocrResult?.referencia ?? null;
  const hasDetectedAmount = detectedAmount > 0;
  const hasDetectedDate = detectedDate !== null;
  const hasDetectedReference = hasDetectedValue(rawDetectedReference);

  const handleUseDetectedAmount = () => {
    if (modalidad === PAGO_MODALIDAD.ADELANTADO || !hasDetectedAmount) return;
    form.setFieldValue('valor', detectedAmount);
  };

  const handleUseDetectedDate = () => {
    if (!detectedDate) return;
    form.setFieldValue('fechaCobro', detectedDate.startOf('day'));
  };

  // The client is immutable when preselected (create from Cliente 360) or in
  // edit mode; only global create lets the operator pick and clear it.
  const clienteInmutable = fixedClienteId != null || mode === 'edit';

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} style={{ width: '100%' }}>
      {/* The payment flow is intentionally single-column; only metric/detail cards use grids. */}
      <Row gutter={[0, token.marginLG]}>
        <Col span={24}>
          <Form.Item
            style={itemStyle}
            name="modalidad"
            label={t('pages.pagos.modalidad')}
            help={
              tipoSuscripcionLoading && clienteId > 0
                ? t('pages.pagos.modalidadConsultandoSuscripcion')
                : modalidadBloqueada !== null
                  ? t('pages.pagos.modalidadBloqueada', {
                      tipo: tipoSuscripcion?.value ?? tipoSuscripcion?.key ?? '',
                    })
                  : prepagoConFacturas
                    ? t('pages.pagos.modalidadPrepagoFacturas')
                    : undefined
            }
          >
            <Segmented
              block
              disabled={(tipoSuscripcionLoading && clienteId > 0) || modalidadBloqueada !== null}
              options={[
                {
                  label: esPrepago
                    ? t('pages.pagos.modalidadPagarFactura')
                    : t('pages.pagos.modalidadNormal'),
                  value: 'NORMAL',
                },
                { label: t('pages.pagos.modalidadAdelantado'), value: 'ADELANTADO' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            style={itemStyle}
            name="idCliente"
            label={t('pages.pagos.cliente')}
            rules={[{ required: true, message: t('pages.pagos.clienteRequired') }]}
          >
            {isCobradorPaymentScope ? (
              <Select
                id="idCliente"
                aria-label={t('pages.pagos.cliente')}
                disabled
                loading={cobradorContextLoading}
                options={
                  cobradorClienteId != null
                    ? [{ value: cobradorClienteId, label: cobradorClienteLabel }]
                    : []
                }
              />
            ) : (
              <ClienteSelect
                id="idCliente"
                aria-label={t('pages.pagos.cliente')}
                preselectedCliente={preselectedCliente}
                disabled={clienteInmutable}
                allowClear={!clienteInmutable}
              />
            )}
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            style={itemStyle}
            name="idTipoPago"
            label={t('pages.pagos.tipoPago')}
            rules={[{ required: true, message: t('pages.pagos.tipoPagoRequired') }]}
          >
            {isCobradorPaymentScope ? (
              <Select
                id="idTipoPago"
                aria-label={t('pages.pagos.tipoPago')}
                placeholder={t('pages.pagos.selectTipoPago')}
                disabled={ocrPending || cobradorContextLoading || cobradorContextError}
                loading={cobradorContextLoading}
                notFoundContent={
                  cobradorContextLoading ? t('common.loading') : t('common.noResults')
                }
                options={(tiposPago ?? []).map((tipo) => ({
                  value: Number(tipo.id),
                  label: tipo.value ?? tipo.key ?? `#${tipo.id}`,
                }))}
              />
            ) : (
              <CatalogoSelect
                category="TIPO_PAGO"
                id="idTipoPago"
                aria-label={t('pages.pagos.tipoPago')}
                placeholder={t('pages.pagos.selectTipoPago')}
                disabled={ocrPending}
              />
            )}
          </Form.Item>
        </Col>

        {clienteId > 0 ? (
          <Col span={24}>
            <ResumenFinanciero
              resumen={resumenFinanciero}
              loading={resumenFinancieroLoading}
              error={resumenFinancieroError}
            />
          </Col>
        ) : null}

        {modalidad === PAGO_MODALIDAD.ADELANTADO ? (
          <>
            <Col span={24}>
              <Form.Item
                style={itemStyle}
                name="mesesAdelantado"
                label={t('pages.pagos.mesesAdelantado')}
                initialValue={6}
                rules={[
                  { required: true, message: t('pages.pagos.mesesRequired') },
                  {
                    type: 'number',
                    min: 1,
                    max: 12,
                    message: t('pages.pagos.mesesRange'),
                  },
                ]}
              >
                <InputNumber min={1} max={12} step={1} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item style={itemStyle} label={t('pages.pagos.totalPlanificado')}>
                <InputNumber
                  value={Number(totalPlanificado.toFixed(2))}
                  disabled
                  precision={2}
                  style={{ width: '100%' }}
                  prefix="Q"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                style={itemStyle}
                name="descuentoTipo"
                label={t('pages.pagos.descuentoTipo')}
              >
                <Radio.Group
                  options={[
                    { label: t('pages.pagos.descuentoMonto'), value: 'MONTO' },
                    { label: t('pages.pagos.descuentoPorcentaje'), value: 'PORCENTAJE' },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                style={itemStyle}
                name="descuentoValor"
                label={t('pages.pagos.descuentoValor')}
              >
                <InputNumber
                  min={0}
                  step={descuentoTipo === 'PORCENTAJE' ? 1 : 0.01}
                  precision={descuentoTipo === 'PORCENTAJE' ? 2 : 2}
                  disabled={!descuentoTipo}
                  placeholder={descuentoTipo === 'PORCENTAJE' ? '%' : 'Q'}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            {prepagoPreview.isError ? (
              <Col span={24}>
                <Alert
                  type="error"
                  showIcon
                  title={t('pages.pagos.prepagoPreview.errorTitle')}
                  description={t('pages.pagos.prepagoPreview.errorDescription')}
                />
              </Col>
            ) : facturaActual ? (
              <Col span={24}>
                <Alert
                  type="warning"
                  showIcon
                  title={t('pages.pagos.prepagoPreview.currentInvoiceTitle')}
                  description={
                    <Space orientation="vertical" size={token.marginXS} style={{ width: '100%' }}>
                      <Form.Item
                        name="reemplazarFacturaActual"
                        valuePropName="checked"
                        initialValue
                        style={itemStyle}
                      >
                        <Checkbox disabled={loading || prepagoPreviewLoading}>
                          {t('pages.pagos.prepagoPreview.replaceCurrentInvoice', {
                            id: facturaActual.id,
                            inicio: facturaActual.fechaInicio,
                            fin: facturaActual.fechaFin,
                            saldo: formatCurrency(Number(facturaActual.saldoPendiente)),
                          })}
                        </Checkbox>
                      </Form.Item>
                      <Text type="secondary">
                        {reemplazarFacturaActual
                          ? t('pages.pagos.prepagoPreview.selectedDescription')
                          : t('pages.pagos.prepagoPreview.unselectedDescription')}
                      </Text>
                    </Space>
                  }
                />
              </Col>
            ) : null}
          </>
        ) : null}

        <Col span={24}>
          <Form.Item
            style={itemStyle}
            name="valor"
            label={
              modalidad === PAGO_MODALIDAD.ADELANTADO
                ? t('pages.pagos.totalAPagar')
                : t('pages.pagos.valor')
            }
            rules={[
              { required: true, message: t('pages.pagos.valorRequired') },
              { type: 'number', min: 0.01, message: t('pages.pagos.valorPositive') },
              // S5 live ceiling: an amount above the marked invoices is refused
              // WHILE a payable invoice is left unmarked.
              { validator: validarTopeMarcado },
            ]}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              disabled={modalidad === PAGO_MODALIDAD.ADELANTADO}
              size="large"
              style={{ width: '100%' }}
              prefix="Q"
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            style={itemStyle}
            name="fechaCobro"
            label={t('pages.pagos.fechaPago')}
            initialValue={dayjs().startOf('day')}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>

        {boletaVisible && (
          <Col span={24}>
            <div style={{ marginBottom: token.marginXS }}>
              <Text style={{ fontSize: 14 }}>{t('pages.pagos.boleta')}</Text>
            </div>
            <Space orientation="vertical" style={{ width: '100%' }} size={token.marginXS}>
              <Space wrap size={[token.marginXS, token.marginXS]}>
                <Upload
                  accept={BOLETA_IMAGE_ACCEPT}
                  capture="environment"
                  showUploadList={false}
                  beforeUpload={handleUpload}
                  disabled={ocrPending}
                >
                  <Button
                    type="primary"
                    icon={<CameraOutlined />}
                    loading={ocrPending}
                    disabled={ocrPending}
                    style={{ minHeight: 44 }}
                  >
                    {t('pages.pagos.takePhoto')}
                  </Button>
                </Upload>
                <Upload
                  accept={BOLETA_IMAGE_ACCEPT}
                  showUploadList={false}
                  beforeUpload={handleUpload}
                  disabled={ocrPending}
                >
                  <Button
                    icon={<PictureOutlined />}
                    loading={ocrPending}
                    disabled={ocrPending}
                    style={{ minHeight: 44 }}
                  >
                    {t('pages.pagos.chooseImage')}
                  </Button>
                </Upload>
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt={t('pages.pagos.boletaPreview')}
                    style={{
                      maxHeight: 72,
                      maxWidth: 120,
                      borderRadius: 6,
                      border: '1px solid rgba(128,128,128,0.35)',
                      objectFit: 'contain',
                    }}
                  />
                )}
                {previewUrl && (
                  <Button
                    size="small"
                    style={{ minHeight: 44 }}
                    onClick={handleRemoveBoleta}
                    disabled={ocrPending}
                  >
                    {t('pages.pagos.removeBoleta')}
                  </Button>
                )}
              </Space>
              <Form.Item
                style={itemStyle}
                name="referenciaBoleta"
                label={t('pages.pagos.boletaReferencia')}
              >
                <Input
                  allowClear
                  placeholder={t('pages.pagos.boletaReferenciaPlaceholder')}
                  disabled={ocrPending}
                  onChange={() => {
                    referenceSourceRef.current = REFERENCE_SOURCE.MANUAL;
                  }}
                />
              </Form.Item>
              {ocrFallido && (
                <Alert type="warning" showIcon title={t('pages.pagos.ocrNoDisponible')} />
              )}
              {ocrResult && !ocrFallido && (
                <Alert
                  type={
                    hasDetectedAmount && hasDetectedDate && hasDetectedReference
                      ? 'info'
                      : 'warning'
                  }
                  showIcon
                  title={t('pages.pagos.ocrEvidenceSummary')}
                  description={
                    <div
                      style={{
                        display: 'grid',
                        gap: token.marginXS,
                        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                          {t('pages.pagos.ocrDetectedAmount')}
                        </Text>
                        <Text
                          type="secondary"
                          style={{ display: 'block', overflowWrap: 'anywhere' }}
                        >
                          {hasDetectedAmount
                            ? formatCurrency(detectedAmount)
                            : hasDetectedValue(rawDetectedAmount)
                              ? rawDetectedAmount
                              : t('pages.pagos.ocrNotDetected')}
                        </Text>
                        {modalidad === PAGO_MODALIDAD.NORMAL && hasDetectedAmount ? (
                          <Button
                            type="link"
                            size="small"
                            style={{ minHeight: 44, paddingInline: 0 }}
                            onClick={handleUseDetectedAmount}
                          >
                            {t('pages.pagos.useDetectedAmount')}
                          </Button>
                        ) : null}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                          {t('pages.pagos.ocrDetectedDate')}
                        </Text>
                        <Text
                          type="secondary"
                          style={{ display: 'block', overflowWrap: 'anywhere' }}
                        >
                          {detectedDate
                            ? detectedDate.format('DD/MM/YYYY')
                            : hasDetectedValue(rawDetectedDate)
                              ? rawDetectedDate
                              : t('pages.pagos.ocrNotDetected')}
                        </Text>
                        {hasDetectedDate ? (
                          <Button
                            type="link"
                            size="small"
                            style={{ minHeight: 44, paddingInline: 0 }}
                            onClick={handleUseDetectedDate}
                          >
                            {t('pages.pagos.useDetectedDate')}
                          </Button>
                        ) : null}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                          {t('pages.pagos.ocrDetectedReference')}
                        </Text>
                        <Text
                          type="secondary"
                          style={{ display: 'block', overflowWrap: 'anywhere' }}
                        >
                          {hasDetectedReference
                            ? rawDetectedReference
                            : t('pages.pagos.ocrNotDetected')}
                        </Text>
                      </div>
                    </div>
                  }
                />
              )}
              <Form.Item hidden name="montoBoleta">
                <Input />
              </Form.Item>
              <Form.Item hidden name="fechaBoleta">
                <Input />
              </Form.Item>
            </Space>
          </Col>
        )}

        {clienteId > 0 &&
        esPrepago &&
        (facturasPreviewLoading || prepagoConFacturas || prepagoConCubiertas) ? (
          <Col span={24}>
            <PagoFacturasPicker
              facturas={facturasPagables}
              cubiertas={facturasCubiertas}
              loading={facturasPreviewLoading && !prepagoConFacturas}
              modoRequerido={modalidad === PAGO_MODALIDAD.NORMAL && facturasPagables.length > 0}
              totalSeleccionado={totalSeleccionado}
              montoIngresado={montoIngresado}
              disabled={loading}
              onModeChange={() => form.setFieldValue('modalidad', PAGO_MODALIDAD.NORMAL)}
              onSelectionChange={() => form.setFieldValue('modalidad', PAGO_MODALIDAD.NORMAL)}
            />
          </Col>
        ) : null}

        {clienteId > 0 &&
        pagoFacturaEnCurso &&
        !facturasPreviewLoading &&
        !facturasPreview.isError &&
        facturasPagables.length === 0 ? (
          <Col span={24}>
            <Alert type="info" showIcon title={t('pages.pagos.facturas.empty')} />
          </Col>
        ) : null}

        {clienteId > 0 && esperandoMarcas ? (
          <Col span={24}>
            <Alert type="info" showIcon title={t('pages.pagos.fifoPreview.marcaParaVer')} />
          </Col>
        ) : null}

        {clienteId > 0 && esPrepago && facturasPreview.isError ? (
          <Col span={24}>
            <Alert
              type="warning"
              showIcon
              title={t('pages.pagos.facturas.errorLecturaTitulo')}
              description={t('pages.pagos.facturas.errorLecturaDescripcion')}
            />
          </Col>
        ) : null}

        {modalidad === PAGO_MODALIDAD.NORMAL && clienteId > 0 && !esperandoMarcas ? (
          <Col span={24}>
            <PagoFifoPreview
              preview={fifoPreview.data}
              hasAmount={montoIngresado > 0}
              loading={fifoPreviewLoading}
              error={fifoPreview.isError}
              onRetry={() => {
                void fifoPreview.refetch();
              }}
            />
          </Col>
        ) : null}

        <Col span={24}>
          <Form.Item name="archivoBoleta" hidden>
            <Input />
          </Form.Item>
          {/*
            Bloque de geolocalización — SOLO cobrador (el admin no necesita
            capturar GPS). Si falta GPS, el backend usa la ubicación del cliente.
            La ubicación se captura automática del
            dispositivo; no hay ingreso manual (regla de negocio 2026-08-16).
          */}
          {isCobradorPaymentScope && (
            <>
              {geoEstado === 'solicitando' && (
                <Alert
                  type="info"
                  showIcon
                  icon={<LoadingOutlined spin />}
                  title={t('pages.pagos.geoSolicitando')}
                />
              )}
              {geoEstado === 'capturada' && ubicacion && (
                <>
                  <Alert
                    type="success"
                    showIcon
                    icon={<CheckCircleOutlined />}
                    title={t('pages.pagos.geoCapturada', {
                      lat: ubicacion.lat.toFixed(6),
                      lng: ubicacion.lng.toFixed(6),
                    })}
                  />
                  {clienteId > 0 && ubicacionPreview.isLoading && (
                    <Alert
                      type="info"
                      showIcon
                      title={t('pages.pagos.ubicacionClienteComparando')}
                    />
                  )}
                  {clienteId > 0 && ubicacionPreview.data?.sugerirActualizacion && (
                    <>
                      <Alert
                        type={ubicacionPreview.data.ubicacionCliente === null ? 'info' : 'warning'}
                        showIcon
                        title={
                          ubicacionPreview.data.ubicacionCliente === null
                            ? t('pages.pagos.ubicacionClienteSinRegistrar')
                            : t('pages.pagos.ubicacionClienteDiferente')
                        }
                        description={
                          ubicacionPreview.data.distanciaMetros == null
                            ? t('pages.pagos.ubicacionClienteSinRegistrarDescripcion')
                            : t('pages.pagos.ubicacionClienteDistancia', {
                                distancia: Math.round(ubicacionPreview.data.distanciaMetros),
                              })
                        }
                      />
                      <div
                        style={{
                          marginTop: token.marginSM,
                          padding: token.paddingSM,
                          border: `1px solid ${token.colorBorderSecondary}`,
                          borderRadius: token.borderRadiusSM,
                          background: token.colorFillQuaternary,
                        }}
                      >
                        <Checkbox
                          checked={clientLocationDecision === 'update'}
                          disabled={loading}
                          onChange={(event) => chooseClientLocationDecision(event.target.checked)}
                        >
                          {ubicacionPreview.data.ubicacionCliente === null
                            ? t('pages.pagos.ubicacionClienteRegistrarCheck')
                            : t('pages.pagos.ubicacionClienteActualizarCheck')}
                        </Checkbox>
                        <Text
                          type={clientLocationDecision === 'update' ? 'success' : 'secondary'}
                          style={{ display: 'block', marginTop: token.marginXXS, fontSize: 12 }}
                        >
                          {clientLocationDecision === 'update'
                            ? t('pages.pagos.ubicacionClienteSeActualizara')
                            : t('pages.pagos.ubicacionClienteNoActualizar')}
                        </Text>
                      </div>
                    </>
                  )}
                </>
              )}
              {(geoEstado === 'denegada' || geoEstado === 'noSoportada') && (
                <Alert
                  type="error"
                  showIcon
                  title={
                    geoEstado === 'noSoportada'
                      ? t('pages.pagos.geoNoSoportada')
                      : t('pages.pagos.geoPermisoDenegado')
                  }
                  description={t('pages.pagos.geoRequerida')}
                  action={
                    <Button
                      size="small"
                      onClick={() =>
                        solicitarUbicacion(isCobradorPaymentScope, setGeoEstado, setUbicacion)
                      }
                    >
                      {t('pages.pagos.geoReintentar')}
                    </Button>
                  }
                />
              )}
            </>
          )}
        </Col>
        <Col span={24}>
          <Form.Item style={itemStyle} name="comentario" label={t('pages.pagos.comentarios')}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Col>
        {!hideSubmit && (
          <Col span={24}>
            <Form.Item style={itemStyle}>
              <Space wrap size={[token.marginXS, token.marginXS]}>
                <Button type="primary" htmlType="submit" loading={loading} disabled={submitBlocked}>
                  {mode === 'create' ? t('pages.pagos.create') : t('common.saveChanges')}
                </Button>
              </Space>
            </Form.Item>
          </Col>
        )}
      </Row>
    </Form>
  );
}
